import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Search, Building2, SortAsc, SortDesc, Filter, X, Users as UsersIcon, Share2, History, Upload, Download, FileSpreadsheet } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { 
  getCompanyReferrals, 
  createCompanyReferral,
  BulkUploadResponse,
  updateCompanyReferralStatus,
  CompanyReferral,
  CreateCompanyReferralRequest,
  UpdateCompanyReferralStatusRequest,
  CompanyReferralFilters,
} from '../../services/companyReferralApi';
import { getAssignedManagers } from '../../services/apiclient';
import { useRbac } from '../../context';
import { cn } from '../../components/lib/utils';

// Define SPOC details interface
interface ISPOCDetails {
  name: string;
  email: string;
  phone: string;
  designation: string;
}

interface CompanyReferralFormData {
  name: string;
  location: string;
  spocDetails: ISPOCDetails[];
}

interface FbdeUserOption {
  id: string;
  username: string;
  email?: string;
  role?: string;
}

interface SPOCErrors {
  name?: string;
  email?: string;
  phone?: string;
  designation?: string;
}

interface FormErrors {
  name?: string;
  location?: string;
  fbdeUserId?: string;
  spocDetails?: SPOCErrors[];
}

// SortableHeader component
const SortableHeader: React.FC<{
  column: string;
  className?: string;
  children: React.ReactNode;
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSort: (field: string) => void;
}> = ({ column, className = "", children, sortBy, sortOrder, onSort }) => {
  return (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-gray-50 sticky top-0 ${className}`}
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column && (
          sortOrder === "asc" ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
        )}
      </div>
    </th>
  );
};

const CompanyReferralProgram: React.FC = () => {
  const [companyReferrals, setCompanyReferrals] = useState<CompanyReferral[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState<CompanyReferralFormData>({
    name: '',
    location: '',
    spocDetails: [{ name: '', email: '', phone: '', designation: '' }],
  });
  
  const [selectedFbdeUser, setSelectedFbdeUser] = useState<FbdeUserOption | null>(null);
  const [fbdeSearchTerm, setFbdeSearchTerm] = useState('');
  const [fbdeSearchResults, setFbdeSearchResults] = useState<FbdeUserOption[]>([]);
  const [isSearchingFbde, setIsSearchingFbde] = useState(false);
  const [showFbdeDropdown, setShowFbdeDropdown] = useState(false);
  const fbdeSelectingRef = useRef(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Filter states
  const [selectedDateFrom, setSelectedDateFrom] = useState('');
  const [selectedDateTo, setSelectedDateTo] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalResults, setTotalResults] = useState(0);
  
  // Company referral details popup state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCompanyReferral, setSelectedCompanyReferral] = useState<CompanyReferral | null>(null);
  
  // Status change modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState({
    companyReferralId: '',
    currentStatus: 'underProcess' as 'underProcess' | 'onboarded' | 'notOnboarded',
    newStatus: 'underProcess' as 'underProcess' | 'onboarded' | 'notOnboarded',
    remarks: ''
  });
  const [statusChangeErrors, setStatusChangeErrors] = useState<{remarks?: string}>({});
  const user = useRbac();
  
  // CSV upload states
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [csvUploadError, setCsvUploadError] = useState<string>('');
  const [csvUploadSuccess, setCsvUploadSuccess] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // CSV preview states
  const [csvPreviewData, setCsvPreviewData] = useState<CreateCompanyReferralRequest[] | null>(null);
  const [csvValidationErrors, setCsvValidationErrors] = useState<Array<{row: number; errors: string[]}>>([]);
  const [showPreview, setShowPreview] = useState(false);

  // Load company referrals on component mount
  useEffect(() => {
    fetchCompanyReferrals();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.fbde-search-container')) {
        setShowFbdeDropdown(false);
      }
    };

    if (showFbdeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFbdeDropdown]);

  const fetchCompanyReferrals = async () => {
    try {
      setLoading(true);
      const filters: CompanyReferralFilters = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        status: selectedStatus !== 'all' ? selectedStatus as 'underProcess' | 'onboarded' | 'notOnboarded' : undefined,
        dateFrom: selectedDateFrom || undefined,
        dateTo: selectedDateTo || undefined,
      };
      
      const response = await getCompanyReferrals(filters);
      setCompanyReferrals(response.companyReferrals);
      setTotalResults(response.totalResults);
    } catch (error) {
      console.error('Error fetching company referrals:', error);
      toast.error('Failed to fetch company referrals');
    } finally {
      setLoading(false);
    }
  };

  const fetchFbdeUsers = useCallback(async (searchValue: string) => {
    try {
      setIsSearchingFbde(true);
      const response = await getAssignedManagers('FBDE', searchValue);
      setFbdeSearchResults(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching FBDE users:', error);
      setFbdeSearchResults([]);
    } finally {
      setIsSearchingFbde(false);
    }
  }, []);

  useEffect(() => {
    if (!showAddModal || user.role !== 'ADMIN') {
      return;
    }

    if (fbdeSelectingRef.current) {
      fbdeSelectingRef.current = false;
      return;
    }

    const debounceId = setTimeout(() => {
      fetchFbdeUsers(fbdeSearchTerm.trim());
    }, 300);

    return () => {
      clearTimeout(debounceId);
    };
  }, [showAddModal, user.role, fbdeSearchTerm, fetchFbdeUsers]);

  const handleFbdeSelect = (fbde: FbdeUserOption) => {
    fbdeSelectingRef.current = true;
    setSelectedFbdeUser(fbde);
    setFbdeSearchTerm(fbde.username);
    setShowFbdeDropdown(false);
  };

  const handleClearSelectedFbde = () => {
    setSelectedFbdeUser(null);
    setFbdeSearchTerm('');
    setShowFbdeDropdown(false);
    fbdeSelectingRef.current = false;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location: '',
      spocDetails: [{ name: '', email: '', phone: '', designation: '' }],
    });
    setFormErrors({});
    setSelectedFbdeUser(null);
    setFbdeSearchTerm('');
    setFbdeSearchResults([]);
    setShowFbdeDropdown(false);
    fbdeSelectingRef.current = false;
  };

  // SPOC handling functions
  const addSpoc = () => {
    setFormData(prev => ({
      ...prev,
      spocDetails: [...prev.spocDetails, { name: '', email: '', phone: '', designation: '' }],
    }));
  };

  const removeSpoc = (index: number) => {
    setFormData(prev => ({
      ...prev,
      spocDetails: prev.spocDetails.filter((_, i) => i !== index),
    }));
  };

  const handleSpocChange = (e: React.ChangeEvent<HTMLInputElement>, spocIndex: number) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      spocDetails: prev.spocDetails.map((spoc, index) =>
        index === spocIndex ? { ...spoc, [name]: value } : spoc
      ),
    }));
  };

  // Load company referrals when search term or filters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchCompanyReferrals();
    }, 500); // Debounce search

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedStatus, selectedDateFrom, selectedDateTo, currentPage, itemsPerPage]);

  // Reset pagination to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchTerm, selectedStatus, selectedDateFrom, selectedDateTo]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Pagination functions
  const totalPages = Math.ceil(totalResults / itemsPerPage);
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Company referral details popup handler
  const handleCompanyReferralClick = (companyReferral: CompanyReferral) => {
    setSelectedCompanyReferral(companyReferral);
    setShowDetailsModal(true);
  };

  const sortedCompanyReferrals = [...companyReferrals].sort((a, b) => {
    let aValue: any = a[sortBy as keyof CompanyReferral];
    let bValue: any = b[sortBy as keyof CompanyReferral];
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedDateFrom('');
    setSelectedDateTo('');
    setSelectedStatus('all');
  };

  // Get active filters
  const getActiveFilters = () => {
    const filters = [];
    if (searchTerm) filters.push({ key: 'search', value: searchTerm, label: 'Search' });
    if (selectedDateFrom) filters.push({ key: 'dateFrom', value: selectedDateFrom, label: 'From Date' });
    if (selectedDateTo) filters.push({ key: 'dateTo', value: selectedDateTo, label: 'To Date' });
    if (selectedStatus !== 'all') filters.push({ key: 'status', value: selectedStatus, label: 'Status' });
    return filters;
  };

  const activeFilters = getActiveFilters();

  // Helper function to get status display info
  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'underProcess':
        return { label: 'Under Process', variant: 'secondary' as const, className: 'bg-yellow-100 text-yellow-800' };
      case 'onboarded':
        return { label: 'Onboarded', variant: 'default' as const, className: 'bg-green-100 text-green-800' };
      case 'notOnboarded':
        return { label: 'Not Onboarded', variant: 'destructive' as const, className: 'bg-red-100 text-red-800' };
      case 'added':
        return { label: 'Added', variant: 'default' as const, className: 'bg-green-100 text-green-800' };
      case 'approched':
        return { label: 'Approched', variant: 'default' as const, className: 'bg-green-100 text-green-800' };
      case 'inTalk':
        return { label: 'In Talk', variant: 'default' as const, className: 'bg-green-100 text-green-800' };
      case 'confirmed':
        return { label: 'Confirmed', variant: 'default' as const, className: 'bg-green-100 text-green-800' };
      case 'activated':
        return { label: 'Activated', variant: 'default' as const, className: 'bg-green-100 text-green-800' };
      case 'onHold':
        return { label: 'On Hold', variant: 'default' as const, className: 'bg-gray-100 text-gray-800' };
      default:
        return { label: status, variant: 'secondary' as const, className: 'bg-gray-100 text-gray-800' };
    }
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Company name is required';
    }
    
    if (!formData.location.trim()) {
      errors.location = 'Location is required';
    }

    // Validate SPOC details
    if (!formData.spocDetails || formData.spocDetails.length === 0) {
      errors.spocDetails = [];
    } else {
      const phoneRegex = /^[6-9]\d{9}$/;  // Indian mobile number validation
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;  // Email validation
      
      errors.spocDetails = formData.spocDetails.map(spoc => {
        const spocErrors: SPOCErrors = {};
        
        // Validate name
        if (!spoc.name.trim()) {
          spocErrors.name = 'Name is required';
        }
        
        // Validate phone
        if (!spoc.phone.trim()) {
          spocErrors.phone = 'Phone number is required';
        } else if (!phoneRegex.test(spoc.phone.trim())) {
          spocErrors.phone = 'Must be a valid 10-digit Indian mobile number starting with 6-9';
        }
        
        // Validate email (optional, but must be valid if provided)
        if (spoc.email.trim() && !emailRegex.test(spoc.email.trim())) {
          spocErrors.email = 'Must be a valid email address';
        }
        
        // Validate designation
        if (!spoc.designation.trim()) {
          spocErrors.designation = 'Designation is required';
        }
        
        return spocErrors;
      });
    }

    setFormErrors(errors);
    
    // Check if there are any errors
    const hasTopLevelErrors = Object.keys(errors).some(key => key !== 'spocDetails');
    const hasSpocErrors = errors.spocDetails && errors.spocDetails.length > 0 && 
                          errors.spocDetails.some(spoc => Object.keys(spoc).length > 0);
    
    return !hasTopLevelErrors && !hasSpocErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
     
      // Add new company referral - send as array with single item
      const createData: CreateCompanyReferralRequest = {
        name: formData.name,
        location: formData.location,
        spocDetails: formData.spocDetails,
      };

      if (user.role === 'ADMIN') {
        createData.referredByFbdeUserId = selectedFbdeUser?.id ?? undefined;
      }
      
      await createCompanyReferral([createData]) as CompanyReferral;
      toast.success('Company referral added successfully');
      setShowAddModal(false);
      
      resetForm();
      fetchCompanyReferrals(); // Refresh the list
    } catch (error: any) {
      console.log('Error saving company referral:', error);
      if(error.status === 409) {
        toast.error('Company referral with this name and location already exists');
      }else{
        toast.error('Failed to save company referral');
      }
      
    } finally {
      setLoading(false);
    }
  };

  const openStatusChangeModal = (companyReferralId: string, currentStatus: 'underProcess' | 'onboarded' | 'notOnboarded') => {
    setStatusChangeData({
      companyReferralId,
      currentStatus,
      newStatus: currentStatus,
      remarks: ''
    });
    setStatusChangeErrors({});
    setShowStatusModal(true);
  };

  const handleStatusChange = async () => {
    // Validate remarks requirement
    const errors: {remarks?: string} = {};
    if (statusChangeData.newStatus === 'notOnboarded' && !statusChangeData.remarks.trim()) {
      errors.remarks = 'Remarks are mandatory when status is set to "Not Onboarded"';
    }

    setStatusChangeErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setLoading(true);
      await updateCompanyReferralStatus(statusChangeData.companyReferralId, {
        status: statusChangeData.newStatus,
        remarks: statusChangeData.remarks
      });
      toast.success('Company referral status updated successfully');
      setShowStatusModal(false);
      fetchCompanyReferrals(); // Refresh the list
    } catch (error) {
      console.error('Error updating company referral status:', error);
      toast.error('Failed to update company referral status');
    } finally {
      setLoading(false);
    }
  };

  // CSV Functions
  const downloadSampleCSV = () => {
    const headers = ['Company Name', 'Location', 'SPOC Name', 'SPOC Phone', 'SPOC Email', 'SPOC Designation'];
    const sampleData = [
      ['Acme Corp', 'Mumbai', 'John Doe', '9876543210', 'john.doe@acme.com', 'HR Manager'],
      ['Tech Solutions', 'Bangalore', 'Jane Smith', '9876543211', 'jane.smith@tech.com', 'Recruitment Head'],
    ];
    
    const csvContent = [
      headers.join(','),
      ...sampleData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'company_referral_sample.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Sample CSV downloaded successfully');
  };

  const parseCSV = (csvText: string): any[] => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const expectedHeaders = ['Company Name', 'Location', 'SPOC Name', 'SPOC Phone', 'SPOC Email', 'SPOC Designation'];
    
    // Check if headers match (case-insensitive)
    const normalizedHeaders = headers.map(h => h.toLowerCase());
    const normalizedExpected = expectedHeaders.map(h => h.toLowerCase());
    
    if (normalizedHeaders.length !== normalizedExpected.length) {
      throw new Error(`Invalid CSV format. Expected headers: ${expectedHeaders.join(', ')}`);
    }

    const data: any[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      if (values.length !== headers.length) continue;
      
      const row: any = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      data.push(row);
    }

    return data;
  };

  const validateCSVData = (csvData: any[]): { valid: boolean; errors: string[]; rowErrors: Array<{row: number; errors: string[]}> } => {
    const errors: string[] = [];
    const rowErrors: Array<{row: number; errors: string[]}> = [];
    const phoneRegex = /^[6-9]\d{9}$/;
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    csvData.forEach((row, index) => {
      const rowNum = index + 2; // +2 because index starts at 0 and we skip header row
      const rowErrorsList: string[] = [];
      
      if (!row['Company Name']?.trim()) {
        const error = `Company Name is required`;
        errors.push(`Row ${rowNum}: ${error}`);
        rowErrorsList.push(error);
      }
      
      if (!row['Location']?.trim()) {
        const error = `Location is required`;
        errors.push(`Row ${rowNum}: ${error}`);
        rowErrorsList.push(error);
      }
      
      if (!row['SPOC Name']?.trim()) {
        const error = `SPOC Name is required`;
        errors.push(`Row ${rowNum}: ${error}`);
        rowErrorsList.push(error);
      }
      
      if (!row['SPOC Phone']?.trim()) {
        const error = `SPOC Phone is required`;
        errors.push(`Row ${rowNum}: ${error}`);
        rowErrorsList.push(error);
      } else if (!phoneRegex.test(row['SPOC Phone'].trim())) {
        const error = `SPOC Phone must be a valid 10-digit Indian mobile number starting with 6-9`;
        errors.push(`Row ${rowNum}: ${error}`);
        rowErrorsList.push(error);
      }
      
      if (row['SPOC Email']?.trim() && !emailRegex.test(row['SPOC Email'].trim())) {
        const error = `SPOC Email must be a valid email address`;
        errors.push(`Row ${rowNum}: ${error}`);
        rowErrorsList.push(error);
      }
      
      if (!row['SPOC Designation']?.trim()) {
        const error = `SPOC Designation is required`;
        errors.push(`Row ${rowNum}: ${error}`);
        rowErrorsList.push(error);
      }

      if (rowErrorsList.length > 0) {
        rowErrors.push({ row: rowNum, errors: rowErrorsList });
      }
    });

    return { valid: errors.length === 0, errors, rowErrors };
  };

  const handleCSVUpload = async (file: File) => {
    setCsvUploadError('');
    setCsvUploadSuccess('');
    setShowPreview(false);
    setCsvPreviewData(null);
    setCsvValidationErrors([]);
    
    if (!file.name.endsWith('.csv')) {
      setCsvUploadError('Please upload a CSV file');
      toast.error('Invalid file type. Please upload a CSV file.');
      return;
    }

    try {
      const text = await file.text();
      const csvData = parseCSV(text);
      
      if (csvData.length === 0) {
        setCsvUploadError('CSV file is empty or has no valid data rows');
        toast.error('CSV file is empty or has no valid data rows');
        return;
      }

      // Check maximum row limit (150 rows)
      if (csvData.length > 150) {
        setCsvUploadError(`CSV file contains ${csvData.length} rows. Maximum 150 rows are allowed per upload.`);
        toast.error(`CSV file contains ${csvData.length} rows. Maximum 150 rows are allowed per upload.`);
        return;
      }

      // Validate CSV data
      const validation = validateCSVData(csvData);
      setCsvValidationErrors(validation.rowErrors);

      // Group by company name and location
      const companyMap = new Map<string, any>();
      
      csvData.forEach(row => {
        const key = `${row['Company Name']?.trim() || ''}_${row['Location']?.trim() || ''}`;
        if (!companyMap.has(key)) {
          companyMap.set(key, {
            name: row['Company Name']?.trim() || '',
            location: row['Location']?.trim() || '',
            spocDetails: []
          });
        }
        
        const company = companyMap.get(key);
        company.spocDetails.push({
          name: row['SPOC Name']?.trim() || '',
          phone: row['SPOC Phone']?.trim() || '',
          email: row['SPOC Email']?.trim() || '',
          designation: row['SPOC Designation']?.trim() || ''
        });
      });

      // Prepare bulk upload data - send as array
      const bulkData: CreateCompanyReferralRequest[] = Array.from(companyMap.values()).map(company => {
        const createData: CreateCompanyReferralRequest = {
          name: company.name,
          location: company.location,
          spocDetails: company.spocDetails,
        };

        if (user.role === 'ADMIN' && selectedFbdeUser) {
          createData.referredByFbdeUserId = selectedFbdeUser.id;
        }

        return createData;
      });

      // Store preview data and show preview
      setCsvPreviewData(bulkData);
      setShowPreview(true);
      
      if (!validation.valid) {
        toast.error(`CSV uploaded with ${validation.rowErrors.length} row(s) with errors. Please review before submitting.`);
      } else {
        toast.success('CSV uploaded successfully. Please review and submit.');
      }

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to parse CSV file';
      setCsvUploadError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleSubmitCSV = async () => {
    if (!csvPreviewData || csvPreviewData.length === 0) {
      toast.error('No data to submit');
      return;
    }

    setLoading(true);
    setCsvUploadError('');
    setCsvUploadSuccess('');
    
    try {
      const result = await createCompanyReferral(csvPreviewData) as BulkUploadResponse;
      const successCount = result.success.length;
      const errorCount = result.failed.length;

      if (successCount > 0) {
        setCsvUploadSuccess(`Successfully imported ${successCount} company referral(s)`);
        toast.success(`Successfully imported ${successCount} company referral(s)`);
        fetchCompanyReferrals();
        // Close modal after successful upload if no errors
        if (errorCount === 0) {
          setTimeout(() => {
            setShowBulkUploadModal(false);
            setCsvUploadError('');
            setCsvUploadSuccess('');
            setShowPreview(false);
            setCsvPreviewData(null);
            setCsvValidationErrors([]);
          }, 2000);
        }
      }

      if (errorCount > 0) {
        const errors = result.failed.map(f => 
          `${f.payload.name} (${f.payload.location}): ${f.error}`
        );
        const errorMsg = `Failed to import ${errorCount} company referral(s):\n${errors.join('\n')}`;
        setCsvUploadError(errorMsg);
        if (successCount === 0) {
          toast.error(`Failed to import ${errorCount} company referral(s)`);
        } else {
          toast.error(`Partially imported: ${successCount} succeeded, ${errorCount} failed`);
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to upload CSV file';
      setCsvUploadError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleCSVUpload(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleCSVUpload(file);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <Building2 className="h-6 w-6 text-orange-600" />
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                Company Referral Program
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mb-3">
                Manage company partnerships and referrals
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => {
                setCsvUploadError('');
                setCsvUploadSuccess('');
                setShowBulkUploadModal(true);
              }}
              variant="outline"
              className="border-orange-300 text-orange-600 hover:bg-orange-50"
            >
              <Upload className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Bulk Upload</span>
              <span className="sm:hidden">Bulk</span>
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Company
            </Button>
          </div>
        </div>

        <div className="mt-3">
          {/* Mobile: Vertical column layout for filters */}
          <div className="block md:hidden space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900"
              />
            </div>
            <div>
              <Select
                value={selectedStatus}
                onValueChange={(value) => setSelectedStatus(value)}
              >
                <SelectTrigger className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="underProcess">Under Process</SelectItem>
                  <SelectItem value="onboarded">Onboarded</SelectItem>
                  <SelectItem value="notOnboarded">Not Onboarded</SelectItem>
                  <SelectItem value="added">Added</SelectItem>
                  <SelectItem value="approched">Approched</SelectItem>
                  <SelectItem value="inTalk">In Talk</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="activated">Activated</SelectItem>
                  <SelectItem value="onHold">On Hold</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
            <Input
              type="date"
              value={selectedDateFrom}
              onChange={(e) => setSelectedDateFrom(e.target.value)}
              className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900"
              title="From Date"
            />
            <Input
              type="date"
              value={selectedDateTo}
              onChange={(e) => setSelectedDateTo(e.target.value)}
              className="w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900"
              title="To Date"
            />
            </div>
          </div>

          {/* Desktop: Regular flex layout */}
          <div className="hidden md:flex flex-wrap gap-2">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900"
              />
            </div>
            <Select
              value={selectedStatus}
              onValueChange={(value) => setSelectedStatus(value)}
            >
              <SelectTrigger className="w-48 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="underProcess">Under Process</SelectItem>
                <SelectItem value="onboarded">Onboarded</SelectItem>
                <SelectItem value="notOnboarded">Not Onboarded</SelectItem>
                <SelectItem value="added">Added</SelectItem>
                <SelectItem value="approched">Approched</SelectItem>
                <SelectItem value="inTalk">In Talk</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="activated">Activated</SelectItem>
                <SelectItem value="onHold">On Hold</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={selectedDateFrom}
              onChange={(e) => setSelectedDateFrom(e.target.value)}
              className="w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900"
              title="From Date"
            />
            <Input
              type="date"
              value={selectedDateTo}
              onChange={(e) => setSelectedDateTo(e.target.value)}
              className="w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900"
              title="To Date"
            />
            {activeFilters.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllFilters}
                className="h-10 border-gray-200 hover:bg-gray-50"
              >
                <X className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col flex-1 min-h-0">
        <div className="bg-background relative flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-auto">
            {/* Mobile Card View */}
            <div className="block md:hidden space-y-4">
              {loading ? (
                // Mobile shimmer loading
                Array.from({ length: 5 }).map((_, index) => (
                  <div key={`mobile-shimmer-${index}`} className="animate-pulse bg-white rounded-lg border border-gray-200 p-4">
                    <div className="space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                      <div className="h-6 bg-gray-200 rounded w-20"></div>
                    </div>
                  </div>
                ))
              ) : sortedCompanyReferrals.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {searchTerm ? 'No companies found matching your search.' : 'No company referrals added yet.'}
                </div>
              ) : (
                sortedCompanyReferrals.map((companyReferral) => (
                  <div key={companyReferral._id} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <button
                          onClick={() => handleCompanyReferralClick(companyReferral)}
                          className="text-left font-medium text-gray-900 hover:text-gray-700 cursor-pointer text-base leading-tight"
                        >
                          {companyReferral.name}
                        </button>
                        <Badge 
                          variant={getStatusInfo(companyReferral.status).variant}
                          className={`${getStatusInfo(companyReferral.status).className} text-xs`}
                        >
                          {getStatusInfo(companyReferral.status).label}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500 font-medium">Location:</span>
                          <p className="text-gray-900">{companyReferral.location}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 font-medium">Date:</span>
                          <p className="text-gray-900">{new Date(companyReferral.createdAt).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 font-medium">Referred By:</span>
                          <p className="text-gray-900">{companyReferral.referredBy?.username || 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 font-medium">Led By:</span>
                          <p className="text-gray-900">{companyReferral.referredBy?.ledby || 'N/A'}</p>
                        </div>
                        {(user.role === "ADMIN" || user.role === "MASTER_BDE") && (
                          <div className="col-span-2">
                            <span className="text-gray-500 font-medium">Actions:</span>
                            <div className="mt-1">
                              <Select
                                value={companyReferral.status}
                                onValueChange={(value) => openStatusChangeModal(companyReferral._id, value as 'underProcess' | 'onboarded' | 'notOnboarded')}
                              >
                                <SelectTrigger className="w-full h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="underProcess">Under Process</SelectItem>
                                  <SelectItem value="onboarded">Onboarded</SelectItem>
                                  <SelectItem value="notOnboarded">Not Onboarded</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead style={{ backgroundColor: '#FFF5F2' }}>
                  <tr>
                    <SortableHeader 
                      column="name" 
                      className="min-w-48"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    >
                      Company Name
                    </SortableHeader>
                    <SortableHeader 
                      column="location" 
                      className="min-w-32"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    >
                      Location
                    </SortableHeader>
                    <SortableHeader 
                      column="createdAt" 
                      className="min-w-32"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    >
                      Created At
                    </SortableHeader>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32">
                      Referred By
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32">
                      Led By
                    </th>
                    <SortableHeader 
                      column="status" 
                      className="min-w-32"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    >
                      Status
                    </SortableHeader>
                    {(user.role === "ADMIN" || user.role === "MASTER_BDE") && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    // Shimmer loading rows
                    Array.from({ length: 11 }).map((_, index) => (
                      <tr key={`shimmer-${index}`} className="animate-pulse h-16">
                        <td className="px-4 py-3">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="h-6 bg-gray-200 rounded w-20"></div>
                        </td>
                        {(user.role === "ADMIN" || user.role === "MASTER_BDE") && (
                          <td className="px-4 py-3">
                            <div className="h-6 bg-gray-200 rounded w-24"></div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : sortedCompanyReferrals.length === 0 ? (
                    <tr className="h-16">
                      <td colSpan={(user.role === "ADMIN" || user.role === "MASTER_BDE") ? 6 : 5} className="px-4 py-12 text-center text-gray-500">
                        {searchTerm ? 'No companies found matching your search.' : 'No company referrals added yet.'}
                      </td>
                    </tr>
                  ) : (
                    sortedCompanyReferrals.map((companyReferral) => (
                      <tr key={companyReferral._id} className="hover:bg-gray-5 border-b border-gray-200 h-16">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          <button
                            onClick={() => handleCompanyReferralClick(companyReferral)}
                            className="text-gray-900 hover:text-gray-700 cursor-pointer text-left font-medium"
                          >
                            {companyReferral.name}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{companyReferral.location}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{new Date(companyReferral.createdAt).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{companyReferral.referredBy?.username || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{companyReferral.referredBy?.ledby || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm">
                          <Badge 
                            variant={getStatusInfo(companyReferral.status).variant}
                            className={getStatusInfo(companyReferral.status).className}
                          >
                            {getStatusInfo(companyReferral.status).label}
                          </Badge>
                        </td>
                        {(user.role === "ADMIN" || user.role === "MASTER_BDE") && (
                          <td className="px-4 py-3 text-sm">
                            <Select
                              value={companyReferral.status}
                              onValueChange={(value) => openStatusChangeModal(companyReferral._id, value as 'underProcess' | 'onboarded' | 'notOnboarded')}
                            >
                              <SelectTrigger className="w-40 h-8 text-xs text-left">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="added">Added</SelectItem>
                                <SelectItem value="approched">Approched</SelectItem>
                                <SelectItem value="inTalk">In Talk</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="activated">Activated</SelectItem>
                                <SelectItem value="onHold">On Hold</SelectItem>
                                <SelectItem value="underProcess">Under Process</SelectItem>
                                <SelectItem value="onboarded">Onboarded</SelectItem>
                                <SelectItem value="notOnboarded">Not Onboarded</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Pagination Controls */}
          <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-gray-200 bg-white gap-3 flex-shrink-0">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Show</span>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => handleItemsPerPageChange(parseInt(value))}
              >
                <SelectTrigger className="w-16 sm:w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-700">per page</span>
            </div>
            
            {totalResults > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-xs sm:text-sm text-gray-700">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalResults)} of {totalResults} results
                </span>
              </div>
            )}
            
            {totalPages > 1 && (
              <div className="flex items-center space-x-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="h-8 w-8 p-0"
                >
                  ←
                </Button>
                
                {/* Mobile: Show fewer page numbers */}
                <div className="hidden sm:flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                    if (pageNum > totalPages) return null;
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-orange-600 hover:bg-orange-700' : ''}`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                {/* Mobile: Show current page and ellipsis */}
                <div className="flex sm:hidden items-center space-x-1">
                  {currentPage > 3 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        className="h-8 w-8 p-0"
                      >
                        1
                      </Button>
                      <span className="text-gray-500 px-1">...</span>
                    </>
                  )}
                  
                  {currentPage > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      className="h-8 w-8 p-0"
                    >
                      {currentPage - 1}
                    </Button>
                  )}
                  
                  <Button
                    variant="default"
                    size="sm"
                    className="h-8 w-8 p-0 bg-orange-600 hover:bg-orange-700"
                  >
                    {currentPage}
                  </Button>
                  
                  {currentPage < totalPages && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      className="h-8 w-8 p-0"
                    >
                      {currentPage + 1}
                    </Button>
                  )}
                  
                  {currentPage < totalPages - 2 && (
                    <>
                      <span className="text-gray-500 px-1">...</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(totalPages)}
                        className="h-8 w-8 p-0"
                      >
                        {totalPages}
                      </Button>
                    </>
                  )}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="h-8 w-8 p-0"
                >
                  →
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>

      {/* Add Company Referral Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="w-[95vw] max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Add New Company Referral</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">

            {user.role === 'ADMIN' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Assign FBDE (optional)</Label>
                <div className="fbde-search-container relative">
                  <Input
                    value={fbdeSearchTerm}
                    onChange={(e) => {
                      setFbdeSearchTerm(e.target.value);
                      setShowFbdeDropdown(true);
                    }}
                    placeholder="Search FBDE by name, email, or user ID"
                    className="w-full text-gray-900"
                    onFocus={() => {
                      setShowFbdeDropdown(true);
                      if (!fbdeSearchResults.length && !isSearchingFbde) {
                        fetchFbdeUsers(fbdeSearchTerm.trim());
                      }
                    }}
                    autoComplete="off"
                  />
                  {isSearchingFbde && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-orange-600" />
                    </div>
                  )}
                  {showFbdeDropdown && (
                    <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                      {isSearchingFbde ? (
                        <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-500">
                          <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-orange-600" />
                          <span>Searching FBDE users...</span>
                        </div>
                      ) : fbdeSearchResults.length > 0 ? (
                        fbdeSearchResults.map((fbde) => {
                          const isSelected = selectedFbdeUser?.id === fbde.id;
                          return (
                            <div
                              key={fbde.id}
                              className={cn(
                                'cursor-pointer border-b border-gray-100 px-4 py-3 last:border-b-0',
                                isSelected
                                  ? 'bg-orange-50 text-orange-600'
                                  : 'hover:bg-gray-50 text-gray-700'
                              )}
                              onClick={() => handleFbdeSelect(fbde)}
                            >
                              <div className="text-sm font-medium">{fbde.username}</div>
                              {fbde.email && (
                                <div className="text-xs text-gray-500">{fbde.email}</div>
                              )}
                              <div className="text-xs text-gray-400">{fbde.role || 'FBDE'}</div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500">
                          No FBDE users found. Try a different search.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {selectedFbdeUser && (
                  <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedFbdeUser.username}</p>
                      {selectedFbdeUser.email && (
                        <p className="text-xs text-gray-500">{selectedFbdeUser.email}</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleClearSelectedFbde}
                      className="text-xs text-orange-600 hover:text-orange-700"
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Basic Information - Two Column Grid for Desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium">
                  Company Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full text-gray-900 ${formErrors.name ? "border-red-500 focus:border-red-500" : ""}`}
                  placeholder="Enter company name"
                />
                {formErrors.name && <p className="text-sm text-red-500">{formErrors.name}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="location" className="text-sm font-medium">
                  Location <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className={`w-full text-gray-900 ${formErrors.location ? "border-red-500 focus:border-red-500" : ""}`}
                  placeholder="Enter location"
                />
                {formErrors.location && <p className="text-sm text-red-500">{formErrors.location}</p>}
              </div>
            </div>
            
            {/* SPOC Details Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">
                  SPOC Details <span className="text-red-500">*</span> (at least one required)
                </Label> 
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addSpoc}
                  className="border-orange-300 text-orange-600 hover:bg-orange-50 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" /> Add SPOC
                </Button>
              </div>
              {formData.spocDetails.map((spoc, index) => (
                <div
                  key={index}
                  className="border border-gray-200 p-4 rounded-md bg-gray-50 relative"
                >
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 text-gray-500 hover:text-red-600 transition-colors"
                    onClick={() => removeSpoc(index)}
                    disabled={formData.spocDetails.length === 1}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`spocName-${index}`} className="text-sm font-medium">
                        SPOC Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id={`spocName-${index}`}
                        name="name"
                        value={spoc.name}
                        onChange={(e) => handleSpocChange(e, index)}
                        className={cn(
                          "w-full border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900",
                          formErrors.spocDetails?.[index]?.name && "border-red-500"
                        )}
                        placeholder="Enter SPOC name"
                      />
                      {formErrors.spocDetails?.[index]?.name && (
                        <p className="text-sm text-red-500 mt-1">{formErrors.spocDetails[index].name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`spocPhone-${index}`} className="text-sm font-medium">
                        SPOC Phone <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id={`spocPhone-${index}`}
                        name="phone"
                        type="tel"
                        value={spoc.phone}
                        maxLength={10}
                        onChange={(e) => handleSpocChange(e, index)}
                        className={cn(
                          "w-full border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900",
                          formErrors.spocDetails?.[index]?.phone && "border-red-500"
                        )}
                        placeholder="Enter SPOC phone"
                      />
                      {formErrors.spocDetails?.[index]?.phone && (
                        <p className="text-sm text-red-500 mt-1">{formErrors.spocDetails[index].phone}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`spocEmail-${index}`} className="text-sm font-medium">
                        SPOC Email
                      </Label>
                      <Input
                        id={`spocEmail-${index}`}
                        name="email"
                        type="email"
                        value={spoc.email}
                        onChange={(e) => handleSpocChange(e, index)}
                        className={cn(
                          "w-full border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900",
                          formErrors.spocDetails?.[index]?.email && "border-red-500"
                        )}
                        placeholder="Enter SPOC email (optional)"
                      />
                      {formErrors.spocDetails?.[index]?.email && (
                        <p className="text-sm text-red-500 mt-1">{formErrors.spocDetails[index].email}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`spocdesignation-${index}`} className="text-sm font-medium">
                        SPOC Designation <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id={`spocdesignation-${index}`}
                        name="designation"
                        value={spoc.designation}
                        onChange={(e) => handleSpocChange(e, index)}
                        className={cn(
                          "w-full border-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-gray-900",
                          formErrors.spocDetails?.[index]?.designation && "border-red-500"
                        )}
                        placeholder="Enter SPOC designation"
                      />
                      {formErrors.spocDetails?.[index]?.designation && (
                        <p className="text-sm text-red-500 mt-1">{formErrors.spocDetails[index].designation}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowAddModal(false)}
                className="w-full sm:w-auto order-2 sm:order-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading} 
                className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 order-1 sm:order-2"
              >
                {loading ? 'Adding...' : 'Add Company'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Company Referral Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="w-[95vw] max-w-4xl mx-auto max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold flex items-center gap-2">
              <Building2 className="h-5 w-5 text-orange-600" />
              Company Referral Details
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-1">
            {selectedCompanyReferral && (
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Company Name</Label>
                      <p className="text-lg font-semibold text-gray-900">{selectedCompanyReferral.name}</p>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Location</Label>
                      <p className="text-gray-900">{selectedCompanyReferral.location}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Status</Label>
                      <Badge
                        variant={getStatusInfo(selectedCompanyReferral.status).variant}
                        className={getStatusInfo(selectedCompanyReferral.status).className}
                      >
                        {getStatusInfo(selectedCompanyReferral.status).label}
                      </Badge>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium text-gray-500">Created At</Label>
                      <p className="text-gray-900">
                        {new Date(selectedCompanyReferral.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Referral Details */}
                {selectedCompanyReferral.referredBy && (
                  <div className="space-y-4">
                    <Label className="text-sm font-medium text-gray-700">Referral Details</Label>
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-gray-500">Name:</Label>
                          <p className="text-gray-900">{selectedCompanyReferral.referredBy.username || 'N/A'}</p>
                        </div>
                        {selectedCompanyReferral.referredBy.email && (
                          <div>
                            <Label className="text-sm font-medium text-gray-500">Email:</Label>
                            <p className="text-gray-900">{selectedCompanyReferral.referredBy.email}</p>
                          </div>
                        )}
                        {selectedCompanyReferral.referredBy.phone && (
                          <div>
                            <Label className="text-sm font-medium text-gray-500">Phone:</Label>
                            <p className="text-gray-900">{selectedCompanyReferral.referredBy.phone}</p>
                          </div>
                        )}
                        {selectedCompanyReferral.referredBy.ledby && (
                          <div>
                            <Label className="text-sm font-medium text-gray-500">Led By:</Label>
                            <p className="text-gray-900">{selectedCompanyReferral.referredBy.ledby}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* SPOC Details */}
                {selectedCompanyReferral.spocDetails && selectedCompanyReferral.spocDetails.length > 0 && (
                  <div className="space-y-4">
                    <Label className="text-sm font-medium text-gray-700">SPOC Details</Label>
                    <div className="space-y-3">
                      {selectedCompanyReferral.spocDetails.map((spoc, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-medium text-gray-500">Name</Label>
                              <p className="text-gray-900">{spoc.name}</p>
                            </div>
                            <div>
                              <Label className="text-sm font-medium text-gray-500">Designation</Label>
                              <p className="text-gray-900">{spoc.designation}</p>
                            </div>
                            {spoc.email && (
                              <div>
                                <Label className="text-sm font-medium text-gray-500">Email</Label>
                                <p className="text-gray-900">{spoc.email}</p>
                              </div>
                            )}
                            <div>
                              <Label className="text-sm font-medium text-gray-500">Phone</Label>
                              <p className="text-gray-900">{spoc.phone}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Remarks */}
                {selectedCompanyReferral.remarks && (
                  <div>
                    <Label className="text-sm font-medium text-gray-500">Remarks</Label>
                    <p className="text-gray-900 break-words">{selectedCompanyReferral.remarks}</p>
                  </div>
                )}

                {/* Status Change History */}
                {selectedCompanyReferral.statusChangeHistory && selectedCompanyReferral.statusChangeHistory.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <History className="h-5 w-5 text-orange-600" />
                      <Label className="text-sm font-medium text-gray-700">Status Change History</Label>
                    </div>
                    <div className="space-y-3">
                      {selectedCompanyReferral.statusChangeHistory.map((history, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-medium text-gray-500">New Status</Label>
                              <Badge
                                variant={getStatusInfo(history.newStatus).variant}
                                className={getStatusInfo(history.newStatus).className}
                              >
                                {getStatusInfo(history.newStatus).label}
                              </Badge>
                            </div>
                            {history.oldStatus && (
                              <div>
                                <Label className="text-sm font-medium text-gray-500">Previous Status</Label>
                                <p className="text-gray-900">{getStatusInfo(history.oldStatus).label}</p>
                              </div>
                            )}
                            <div>
                              <Label className="text-sm font-medium text-gray-500">Updated By</Label>
                              <p className="text-gray-900">{history.updatedBy || 'Unknown'}</p>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-sm font-medium text-gray-500">Updated At</Label>
                              <p className="text-gray-900">
                                {new Date(history.updatedAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            {history.remarks && (
                              <div>
                                <Label className="text-sm font-medium text-gray-500">Remarks</Label>
                                <p className="text-gray-900 break-words">{history.remarks}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                    <p className="text-gray-500 text-center">No status history available for this company referral.</p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4 mt-4 justify-end">
            <Button 
              variant="outline" 
              onClick={() => setShowDetailsModal(false)}
              className="w-full sm:w-auto"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="w-[95vw] max-w-2xl mx-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Change Company Referral Status</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Current Status Display */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-500">Current Status</Label>
              <Badge
                variant={getStatusInfo(statusChangeData.currentStatus).variant}
                className={getStatusInfo(statusChangeData.currentStatus).className}
              >
                {getStatusInfo(statusChangeData.currentStatus).label}
              </Badge>
            </div>

            {/* New Status Selection */}
            <div className="space-y-2">
              <Label htmlFor="newStatus" className="text-sm font-medium">
                New Status <span className="text-red-500">*</span>
              </Label>
              <Select
                value={statusChangeData.newStatus}
                onValueChange={(value) => setStatusChangeData(prev => ({ ...prev, newStatus: value as 'underProcess' | 'onboarded' | 'notOnboarded' }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="added">Added</SelectItem>
                  <SelectItem value="approched">Approched</SelectItem>
                  <SelectItem value="inTalk">In Talk</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="activated">Activated</SelectItem>
                  <SelectItem value="onHold">On Hold</SelectItem>
                  <SelectItem value="underProcess">Under Process</SelectItem>
                  <SelectItem value="onboarded">Onboarded</SelectItem>
                  <SelectItem value="notOnboarded">Not Onboarded</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Remarks Field */}
            <div className="space-y-2">
              <Label htmlFor="remarks" className="text-sm font-medium">
                Remarks {statusChangeData.newStatus === 'notOnboarded' && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                id="remarks"
                value={statusChangeData.remarks}
                onChange={(e) => setStatusChangeData(prev => ({ ...prev, remarks: e.target.value }))}
                className={`w-full min-h-[100px] text-gray-900 ${statusChangeErrors.remarks ? "border-red-500 focus:border-red-500" : ""}`}
                placeholder={statusChangeData.newStatus === 'notOnboarded' ? "Please provide remarks for why the company is not onboarded..." : "Optional remarks about the status change..."}
              />
              {statusChangeErrors.remarks && (
                <p className="text-sm text-red-500">{statusChangeErrors.remarks}</p>
              )}
            </div>
          </div>
          
          <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowStatusModal(false)}
              className="w-full sm:w-auto order-2 sm:order-1"
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={handleStatusChange}
              disabled={loading} 
              className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 order-1 sm:order-2"
            >
              {loading ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Modal */}
      <Dialog open={showBulkUploadModal} onOpenChange={setShowBulkUploadModal}>
        <DialogContent className="w-[95vw] max-w-2xl mx-auto max-h-[90vh] flex flex-col p-6">
          <DialogHeader className="flex-shrink-0 pb-4">
            <DialogTitle className="text-lg font-semibold flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-gray-600" />
              Bulk Upload Company Referrals
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 overflow-y-auto flex-1 min-h-0 pr-2 -mr-2">
            {/* Download Sample CSV */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <Label className="text-sm font-medium text-gray-900">Need a template?</Label>
                <p className="text-xs text-gray-600 mt-1">Download a sample CSV file with the correct format</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={downloadSampleCSV}
                className="border-gray-300 text-gray-700 hover:bg-gray-100"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Sample CSV
              </Button>
            </div>

            {/* FBDE Assignment (for ADMIN) */}
            {user.role === 'ADMIN' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Assign FBDE (optional)</Label>
                <div className="fbde-search-container relative">
                  <Input
                    value={fbdeSearchTerm}
                    onChange={(e) => {
                      setFbdeSearchTerm(e.target.value);
                      setShowFbdeDropdown(true);
                    }}
                    placeholder="Search FBDE by name, email, or user ID"
                    className="w-full text-gray-900"
                    onFocus={() => {
                      setShowFbdeDropdown(true);
                      if (!fbdeSearchResults.length && !isSearchingFbde) {
                        fetchFbdeUsers(fbdeSearchTerm.trim());
                      }
                    }}
                    autoComplete="off"
                  />
                  {isSearchingFbde && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-gray-600" />
                    </div>
                  )}
                  {showFbdeDropdown && (
                    <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                      {isSearchingFbde ? (
                        <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-500">
                          <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-gray-600" />
                          <span>Searching FBDE users...</span>
                        </div>
                      ) : fbdeSearchResults.length > 0 ? (
                        fbdeSearchResults.map((fbde) => {
                          const isSelected = selectedFbdeUser?.id === fbde.id;
                          return (
                            <div
                              key={fbde.id}
                              className={cn(
                                'cursor-pointer border-b border-gray-100 px-4 py-3 last:border-b-0',
                                isSelected
                                  ? 'bg-gray-100 text-gray-900'
                                  : 'hover:bg-gray-50 text-gray-700'
                              )}
                              onClick={() => handleFbdeSelect(fbde)}
                            >
                              <div className="text-sm font-medium">{fbde.username}</div>
                              {fbde.email && (
                                <div className="text-xs text-gray-500">{fbde.email}</div>
                              )}
                              <div className="text-xs text-gray-400">{fbde.role || 'FBDE'}</div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500">
                          No FBDE users found. Try a different search.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {selectedFbdeUser && (
                  <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{selectedFbdeUser.username}</p>
                      {selectedFbdeUser.email && (
                        <p className="text-xs text-gray-500">{selectedFbdeUser.email}</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleClearSelectedFbde}
                      className="text-xs text-gray-600 hover:text-gray-700"
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Drag and Drop Area - Only show if no preview */}
            {!showPreview && (
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={cn(
                  "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
                  isDragOver
                    ? "border-gray-400 bg-gray-100"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
                )}
              >
                <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-gray-600" />
                <p className="text-base font-medium text-gray-900 mb-2">
                  Drag and drop a CSV file here
                </p>
                <p className="text-sm text-gray-600 mb-4">or</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-gray-300 text-gray-700 hover:bg-gray-100"
                >
                  <Upload className="h-5 w-5 mr-2" />
                  Browse Files
                </Button>
                <p className="text-xs text-gray-500 mt-4">
                  CSV format: Company Name, Location, SPOC Name, SPOC Phone, SPOC Email, SPOC Designation
                </p>
              </div>
            )}

            {/* Preview Section */}
            {showPreview && csvPreviewData && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Preview Data</h3>
                    <p className="text-sm text-gray-600">
                      {csvPreviewData.length} company referral(s) ready to import
                      {csvValidationErrors.length > 0 && (
                        <span className="text-orange-600 ml-2">
                          ({csvValidationErrors.length} row(s) with validation errors)
                        </span>
                      )}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowPreview(false);
                      setCsvPreviewData(null);
                      setCsvValidationErrors([]);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="text-gray-600 hover:text-gray-700"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                </div>

                {/* Validation Errors Summary */}
                {csvValidationErrors.length > 0 && (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg max-h-40 overflow-y-auto">
                    <p className="text-sm font-medium text-orange-800 mb-2">Validation Errors:</p>
                    <div className="space-y-1">
                      {csvValidationErrors.map((rowError, idx) => (
                        <div key={idx} className="text-sm text-orange-700">
                          <span className="font-medium">Row {rowError.row}:</span> {rowError.errors.join(', ')}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preview Table */}
                <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Company</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">SPOCs</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {csvPreviewData.map((company, index) => {
                        const hasErrors = csvValidationErrors.some(err => 
                          err.row === index + 2 || 
                          csvValidationErrors.some(e => e.row === index + 2)
                        );
                        return (
                          <tr key={index} className={hasErrors ? 'bg-orange-50' : ''}>
                            <td className="px-4 py-3 text-sm text-gray-900">{company.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{company.location}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              <div className="space-y-1">
                                {company.spocDetails.map((spoc, spocIdx) => (
                                  <div key={spocIdx} className="text-xs">
                                    <span className="font-medium">{spoc.name}</span> - {spoc.designation}
                                    {spoc.email && <span className="text-gray-500"> ({spoc.email})</span>}
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {hasErrors ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  Has Errors
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Valid
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Error Message */}
            {csvUploadError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg max-h-60 overflow-y-auto">
                <p className="text-sm font-medium text-red-800 mb-2">Upload Errors:</p>
                <p className="text-sm text-red-700 whitespace-pre-line">{csvUploadError}</p>
              </div>
            )}
            
            {/* Success Message */}
            {csvUploadSuccess && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-medium text-green-800 mb-1">Success!</p>
                <p className="text-sm text-green-700">{csvUploadSuccess}</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4 mt-4 border-t flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowBulkUploadModal(false);
                setCsvUploadError('');
                setCsvUploadSuccess('');
                setIsDragOver(false);
                setShowPreview(false);
                setCsvPreviewData(null);
                setCsvValidationErrors([]);
              }}
              className="w-full sm:w-auto order-2 sm:order-1"
            >
              {showPreview ? 'Cancel' : 'Close'}
            </Button>
            {showPreview && csvPreviewData && (
              <Button
                type="button"
                onClick={handleSubmitCSV}
                disabled={loading}
                className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 order-1 sm:order-2"
              >
                {loading ? 'Submitting...' : `Submit ${csvPreviewData.length} Company Referral(s)`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyReferralProgram;

